/**
 * Iter 116 — Clinician self-serve availability panel.
 *
 * Tabs:
 *   1. Weekly schedule — start / end / break per weekday. Saves via
 *      PUT /api/clinicians/{me}/availability. Applies immediately.
 *   2. Time-off requests — clinician submits a date range (creates a
 *      block with status="pending"). Admin must approve via
 *      PATCH /api/clinicians/{me}/blocks/{block_id}/status before
 *      it blocks patient bookings.
 *   3. Buffer — minutes to keep free between back-to-back appointments.
 *      Applies immediately (self-serve).
 */
import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { CalendarDays, Clock, PlaneTakeoff, CheckCircle2, XCircle, Trash2, Plus, AlertCircle } from 'lucide-react';
import { safeConfirm } from '../lib/confirm';

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const dayDefaults = () => ({
  start_time: '09:00', end_time: '17:00',
  break_start: '12:00', break_end: '13:00',
  slot_step_min: 15,
});

function StatusPill({ status }) {
  const map = {
    approved: { bg: 'bg-emerald-100', fg: 'text-emerald-800', icon: CheckCircle2, label: 'Approved' },
    pending: { bg: 'bg-amber-100', fg: 'text-amber-800', icon: Clock, label: 'Pending' },
    rejected: { bg: 'bg-red-100', fg: 'text-red-800', icon: XCircle, label: 'Rejected' },
  };
  const s = map[status] || map.pending;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${s.bg} ${s.fg}`} data-testid={`avail-status-${status}`}>
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
}

function WeeklyScheduleTab({ clinicianId }) {
  const [days, setDays] = useState(() => Array(7).fill(null));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    let alive = true;
    api.get(`/clinicians/${clinicianId}/availability`).then(r => {
      if (!alive) return;
      const next = Array(7).fill(null);
      r.data.forEach(w => {
        if (next[w.weekday] == null) {
          next[w.weekday] = {
            start_time: w.start_time, end_time: w.end_time,
            break_start: w.break_start || '', break_end: w.break_end || '',
            slot_step_min: w.slot_step_min || 15,
          };
        }
      });
      setDays(next);
      setLoading(false);
    }).catch(() => setLoading(false));
    return () => { alive = false; };
  }, [clinicianId]);

  const toggleDay = (idx) =>
    setDays(d => d.map((x, i) => i === idx ? (x ? null : dayDefaults()) : x));
  const setDay = (idx, patch) =>
    setDays(d => d.map((x, i) => i === idx ? { ...(x || dayDefaults()), ...patch } : x));

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const windows = [];
      days.forEach((d, weekday) => {
        if (!d) return;
        windows.push({
          weekday,
          start_time: d.start_time, end_time: d.end_time,
          break_start: d.break_start || null,
          break_end: d.break_end || null,
          slot_step_min: parseInt(d.slot_step_min, 10) || 15,
          manual_slots: null,
        });
      });
      await api.put(`/clinicians/${clinicianId}/availability`, { windows });
      setMsg('Saved — patients will see the updated hours instantly.');
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Could not save schedule.');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-fg-muted py-8 text-center">Loading…</div>;

  return (
    <div className="space-y-3" data-testid="weekly-schedule-tab">
      <p className="text-sm text-fg-muted">
        Tick the days you&apos;re available. Changes apply immediately to your public booking page.
      </p>
      {WEEKDAYS.map((label, i) => {
        const d = days[i];
        return (
          <div key={i} className="border border-soft rounded-lg p-3" data-testid={`day-row-${i}`}>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 min-w-[85px]">
                <input
                  type="checkbox"
                  checked={!!d}
                  onChange={() => toggleDay(i)}
                  className="w-4 h-4"
                  data-testid={`day-toggle-${i}`}
                />
                <span className="font-semibold text-sm">{label}</span>
              </label>
              {d ? (
                <div className="flex flex-wrap gap-2 items-center flex-1">
                  <div className="flex items-center gap-1">
                    <input type="time" value={d.start_time} onChange={e => setDay(i, { start_time: e.target.value })} className="input py-1 px-2 text-sm" data-testid={`day-start-${i}`} />
                    <span className="text-fg-muted text-sm">–</span>
                    <input type="time" value={d.end_time} onChange={e => setDay(i, { end_time: e.target.value })} className="input py-1 px-2 text-sm" data-testid={`day-end-${i}`} />
                  </div>
                  <div className="text-xs text-fg-muted">Break:</div>
                  <div className="flex items-center gap-1">
                    <input type="time" value={d.break_start} onChange={e => setDay(i, { break_start: e.target.value })} className="input py-1 px-2 text-sm" data-testid={`day-break-start-${i}`} />
                    <span className="text-fg-muted text-sm">–</span>
                    <input type="time" value={d.break_end} onChange={e => setDay(i, { break_end: e.target.value })} className="input py-1 px-2 text-sm" data-testid={`day-break-end-${i}`} />
                  </div>
                  <select value={d.slot_step_min} onChange={e => setDay(i, { slot_step_min: parseInt(e.target.value, 10) })} className="input py-1 px-2 text-sm" data-testid={`day-slot-step-${i}`}>
                    <option value={15}>15 min slots</option>
                    <option value={30}>30 min slots</option>
                    <option value={45}>45 min slots</option>
                    <option value={60}>60 min slots</option>
                  </select>
                </div>
              ) : (
                <span className="text-sm text-fg-muted">Not available</span>
              )}
            </div>
          </div>
        );
      })}
      <div className="pt-2 flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="px-4 py-2 rounded-md bg-[#003D80] text-white text-sm font-semibold hover:bg-[#002A5A] disabled:opacity-60"
          data-testid="save-weekly-schedule"
        >
          {saving ? 'Saving…' : 'Save weekly schedule'}
        </button>
        {msg && <span className="text-sm text-fg-muted" data-testid="save-schedule-msg">{msg}</span>}
      </div>
    </div>
  );
}

function TimeOffTab({ clinicianId }) {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nb, setNb] = useState({ starts_at: '', ends_at: '', reason: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/clinicians/${clinicianId}/blocks`);
      setBlocks(r.data || []);
    } catch (_) { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, [clinicianId]);

  const submit = async (e) => {
    e.preventDefault();
    setMsg('');
    if (!nb.starts_at || !nb.ends_at) {
      setMsg('Please pick start and end dates.');
      return;
    }
    if (new Date(nb.starts_at) >= new Date(nb.ends_at)) {
      setMsg('End must be after start.');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/clinicians/${clinicianId}/blocks`, {
        starts_at: new Date(nb.starts_at).toISOString(),
        ends_at: new Date(nb.ends_at).toISOString(),
        reason: nb.reason || null,
      });
      setNb({ starts_at: '', ends_at: '', reason: '' });
      setMsg('Request submitted — waiting for admin approval.');
      await load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Could not submit request.');
    } finally { setBusy(false); }
  };

  const del = async (block_id) => {
    if (!await safeConfirm('Cancel this time-off request?')) return;
    try {
      await api.delete(`/clinicians/${clinicianId}/blocks/${block_id}`);
      await load();
    } catch (e) { setMsg(e?.response?.data?.detail || 'Could not cancel'); }
  };

  return (
    <div className="space-y-4" data-testid="time-off-tab">
      <form onSubmit={submit} className="border border-soft rounded-lg p-4 space-y-3 bg-soft/40">
        <div className="text-sm font-semibold text-fg">Request time off</div>
        <p className="text-xs text-fg-muted -mt-2">Submit vacation, sick days or holidays. Requests need admin approval before they block patient bookings.</p>
        <div className="grid md:grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-xs text-fg-muted mb-1">Starts</span>
            <input type="datetime-local" value={nb.starts_at} onChange={e => setNb(v => ({ ...v, starts_at: e.target.value }))} className="input w-full" required data-testid="time-off-start" />
          </label>
          <label className="block">
            <span className="block text-xs text-fg-muted mb-1">Ends</span>
            <input type="datetime-local" value={nb.ends_at} onChange={e => setNb(v => ({ ...v, ends_at: e.target.value }))} className="input w-full" required data-testid="time-off-end" />
          </label>
        </div>
        <label className="block">
          <span className="block text-xs text-fg-muted mb-1">Reason (optional)</span>
          <input type="text" value={nb.reason} onChange={e => setNb(v => ({ ...v, reason: e.target.value }))} placeholder="e.g. Family holiday, medical appt, CPD course" className="input w-full" data-testid="time-off-reason" />
        </label>
        {msg && (
          <div className="flex items-start gap-2 text-sm" data-testid="time-off-msg">
            <AlertCircle className="w-4 h-4 text-[#003D80] mt-0.5" />
            <span>{msg}</span>
          </div>
        )}
        <button type="submit" disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#003D80] text-white text-sm font-semibold hover:bg-[#002A5A] disabled:opacity-60" data-testid="submit-time-off">
          <Plus className="w-4 h-4" /> {busy ? 'Submitting…' : 'Submit request'}
        </button>
      </form>

      <div>
        <div className="text-sm font-semibold text-fg mb-2">Your requests</div>
        {loading ? (
          <div className="text-fg-muted py-4 text-center text-sm">Loading…</div>
        ) : blocks.length === 0 ? (
          <div className="text-fg-muted py-4 text-center text-sm border border-dashed border-soft rounded-lg" data-testid="time-off-empty">
            No time-off requests yet.
          </div>
        ) : (
          <div className="space-y-2">
            {blocks.map(b => (
              <div key={b.block_id} className="border border-soft rounded-lg p-3 flex items-start gap-3" data-testid={`block-row-${b.block_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <StatusPill status={b.status || 'approved'} />
                    <span className="text-xs text-fg-muted">
                      {new Date(b.starts_at).toLocaleString()} → {new Date(b.ends_at).toLocaleString()}
                    </span>
                  </div>
                  {b.reason && <div className="text-sm text-fg">{b.reason}</div>}
                  {b.status_note && <div className="text-xs text-fg-muted italic mt-1">Admin note: {b.status_note}</div>}
                </div>
                {(b.status || 'approved') !== 'approved' && (
                  <button onClick={() => del(b.block_id)} className="text-red-600 hover:text-red-800 p-1" title="Cancel" data-testid={`del-block-${b.block_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function BufferTab({ clinicianId }) {
  const [val, setVal] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    api.get(`/clinicians/${clinicianId}/buffer`).then(r => {
      setVal(r.data.buffer_min ?? 0);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [clinicianId]);

  const save = async () => {
    setMsg('');
    try {
      await api.put(`/clinicians/${clinicianId}/buffer`, { buffer_min: parseInt(val, 10) || 0 });
      setMsg('Saved. Patients will see the buffer applied on your next booking.');
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Could not save.');
    }
  };

  if (!loaded) return <div className="text-fg-muted py-8 text-center">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="buffer-tab">
      <p className="text-sm text-fg-muted">
        Minutes to keep free between appointments — useful for note-taking, room reset or a breather.
      </p>
      <div className="border border-soft rounded-lg p-4 max-w-md">
        <label className="block text-sm font-semibold mb-2">Buffer between appointments</label>
        <div className="flex items-center gap-3">
          <input
            type="number"
            min={0}
            max={120}
            step={5}
            value={val}
            onChange={e => setVal(e.target.value)}
            className="input w-24"
            data-testid="buffer-input"
          />
          <span className="text-sm text-fg-muted">minutes</span>
        </div>
        <div className="text-xs text-fg-muted mt-2">Common values: 5, 10, 15. Max 120.</div>
      </div>
      <div className="flex items-center gap-3">
        <button
          onClick={save}
          className="px-4 py-2 rounded-md bg-[#003D80] text-white text-sm font-semibold hover:bg-[#002A5A]"
          data-testid="save-buffer"
        >
          Save buffer
        </button>
        {msg && <span className="text-sm text-fg-muted" data-testid="buffer-msg">{msg}</span>}
      </div>
    </div>
  );
}

export default function MyAvailability() {
  const { user } = useAuth();
  const [tab, setTab] = useState('schedule');

  if (!user) return null;
  const clinicianId = user.user_id;
  const isClinician = user.role === 'clinician' || user.role === 'admin';

  if (!isClinician) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="border border-soft rounded-lg p-6 text-center text-fg-muted">
          Only clinicians can manage availability here.
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="my-availability-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.15em] text-[#003D80] font-medium">Schedule & time off</div>
        <h1 className="text-3xl font-heading font-bold mt-1" data-testid="my-availability-title">My availability</h1>
        <p className="text-sm text-fg-muted mt-1">
          Manage your working hours, request time off and set buffer times between appointments.
        </p>
      </div>

      <div className="flex gap-1 border-b border-soft" data-testid="my-availability-tabs">
        {[
          { key: 'schedule', label: 'Weekly schedule', icon: CalendarDays },
          { key: 'time-off', label: 'Time-off requests', icon: PlaneTakeoff },
          { key: 'buffer', label: 'Buffer', icon: Clock },
        ].map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm border-b-2 transition ${tab === t.key ? 'border-[#003D80] text-[#003D80] font-semibold' : 'border-transparent text-fg-muted hover:text-fg'}`}
              data-testid={`avail-tab-${t.key}`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'schedule' && <WeeklyScheduleTab clinicianId={clinicianId} />}
      {tab === 'time-off' && <TimeOffTab clinicianId={clinicianId} />}
      {tab === 'buffer' && <BufferTab clinicianId={clinicianId} />}
    </div>
  );
}
